/**
 * Map for the public Voucher Redemption Locator: numbered pins matching the result cards, pan/zoom,
 * and a Map / Satellite toggle (BRD 6).
 *
 * Google Maps JavaScript API (owner 2026-10-15, replacing OpenStreetMap + Esri imagery), matching
 * marketing's example (docs/evidence/locator-google-maps-2026-10-15/marketing-example-map-2026-10-16.jpg,
 * PDF note 14): Google's STANDARD roadmap styling (no custom style), Google's own Map | Satellite
 * control top-left, fullscreen top-right, camera control + Street View pegman bottom-right, Google logo
 * bottom-left (drawn by the API; must stay visible).
 *  - Satellite = Google's STANDARD control (no mapTypeIds restriction): its "Labels" box is ticked by
 *    default, so Satellite shows road/town names (hybrid). Owner 2026-10-16: "show satellite labels".
 *  - clickableIcons stays false: Google's default POIs remain VISIBLE (as in the example) but clicking
 *    one does not open a Google place card on top of our partner popup. Not a styling change.
 *  - Pins: round 34px navy HTML markers with bigger white numbers, drawn through google.maps
 *    OverlayView. Not AdvancedMarkerElement — it requires a Map ID, which we do not have; not the
 *    classic google.maps.Marker — deprecated since 2024. An OverlayView of plain DOM keeps the exact
 *    `.vl-pin` look, keyboard access and the card<->pin linking.
 *  - The script is loaded only when this component mounts (googleMapsLoader.ts).
 *
 * If there is no key, Google cannot be reached, or Google rejects the key, the map area shows a short
 * notice instead; the results list (rendered by the page, not here) is unaffected.
 */
/// <reference types="google.maps" />
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  addressLine,
  spreadStackedPins,
  type LocatorLocation,
} from "../../utils/voucherLocator";
import {
  googleMapsAuthFailed,
  loadGoogleMaps,
  onGoogleMapsAuthFailure,
  type GoogleMapsApi,
} from "./googleMapsLoader";

// Types come from the pinned official @types/google.maps (external audit P1, be46519e: a hand-typed
// slice let a non-existent method through tsc). These are TYPE-only references; at runtime every
// Google object comes from the loaded API (`g`), never the global `google`.
type LatLngLiteral = google.maps.LatLngLiteral;
type GMap = google.maps.Map;
type GInfoWindow = google.maps.InfoWindow;
type GLatLng = google.maps.LatLng;
type GOverlayView = google.maps.OverlayView;

/** Round navy marker with the number centred (marketing 2026-10-14: bigger numbers, Google-style). */
const PIN_SIZE = 34;
const MAX_FIT_ZOOM = 13;
const US_CENTER: LatLngLiteral = { lat: 39.5, lng: -98.35 };

const BROWSER_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as
  | string
  | undefined;

type Status = "loading" | "ready" | "unavailable";

interface PinOverlay extends GOverlayView {
  setActive(active: boolean): void;
}

type PinCtor = new (
  pos: LatLngLiteral,
  n: number,
  label: string,
  onActivate: () => void,
) => PinOverlay;

const pinClassCache = new WeakMap<GoogleMapsApi, PinCtor>();

/** One HTML pin per location, positioned by Google's projection. Built after the API has loaded. */
function pinClass(g: GoogleMapsApi): PinCtor {
  const cached = pinClassCache.get(g);
  if (cached) {
    return cached;
  }
  // Field names are long and prefixed: Google's minified MVCObject writes short own properties on
  // the same object, so `n` / `el` could collide.
  class Pin extends g.OverlayView implements PinOverlay {
    private readonly vlPinEl: HTMLDivElement;
    private readonly vlPinLatLng: GLatLng;
    private readonly vlPinN: number;

    constructor(
      pos: LatLngLiteral,
      n: number,
      label: string,
      onActivate: () => void,
    ) {
      super();
      this.vlPinN = n;
      this.vlPinLatLng = new g.LatLng(pos.lat, pos.lng);
      const el = document.createElement("div");
      el.className = "vl-pin";
      el.setAttribute("role", "button");
      el.setAttribute("tabindex", "0");
      el.setAttribute("aria-label", label);
      el.style.position = "absolute";
      el.style.zIndex = String(100 - n);
      const num = document.createElement("span");
      num.textContent = String(n);
      el.appendChild(num);
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        onActivate();
      });
      el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onActivate();
        }
      });
      g.OverlayView.preventMapHitsAndGesturesFrom(el);
      this.vlPinEl = el;
    }

    onAdd() {
      this.getPanes()?.overlayMouseTarget.appendChild(this.vlPinEl);
    }

    draw() {
      const pt = this.getProjection()?.fromLatLngToDivPixel(this.vlPinLatLng);
      if (!pt) {
        return;
      }
      this.vlPinEl.style.left = `${pt.x - PIN_SIZE / 2}px`;
      this.vlPinEl.style.top = `${pt.y - PIN_SIZE / 2}px`;
    }

    onRemove() {
      this.vlPinEl.remove();
    }

    setActive(active: boolean) {
      this.vlPinEl.classList.toggle("vl-pin--active", active);
      this.vlPinEl.style.zIndex = active ? "1000" : String(100 - this.vlPinN);
    }
  }
  pinClassCache.set(g, Pin);
  return Pin;
}

/** Popup body built from text nodes — partner names/addresses are never parsed as HTML. */
function popupContent(p: {
  n: number;
  name: string;
  approximate?: boolean;
  line: string;
}): HTMLElement {
  const box = document.createElement("div");
  box.className = "vl-popup";
  const title = document.createElement("strong");
  title.textContent = `${p.n}. ${p.name}`;
  box.appendChild(title);
  box.appendChild(document.createElement("br"));
  box.appendChild(document.createTextNode(p.line));
  if (p.approximate) {
    box.appendChild(document.createElement("br"));
    const em = document.createElement("em");
    em.textContent = "Approximate location";
    box.appendChild(em);
  }
  return box;
}

interface Props {
  locations: LocatorLocation[];
  center: { lat: number; lng: number } | null;
  activeN: number | null;
  onSelect: (n: number) => void;
}

const LocatorMapInner: React.FC<Props> = ({
  locations,
  center,
  activeN,
  onSelect,
}) => {
  const pins = useMemo(() => spreadStackedPins(locations), [locations]);

  const [status, setStatus] = useState<Status>(() =>
    BROWSER_KEY?.trim() && !googleMapsAuthFailed() ? "loading" : "unavailable",
  );
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<GoogleMapsApi | null>(null);
  const mapRef = useRef<GMap | null>(null);
  const infoRef = useRef<GInfoWindow | null>(null);
  const overlaysRef = useRef<Map<number, PinOverlay>>(new Map());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  // First-render values for map creation; later changes are applied by the effects below.
  const initialRef = useRef({ center });

  // Google may reject the key AFTER the map has drawn (RefererNotAllowedMapError, billing, API not
  // enabled): listen for the whole time the map is mounted, not only while loading.
  useEffect(
    () =>
      onGoogleMapsAuthFailure(() => {
        mapRef.current = null;
        infoRef.current = null;
        apiRef.current = null;
        setStatus("unavailable");
      }),
    [],
  );

  // Load Google and create the map once. Any failure -> "unavailable" (list-only page).
  useEffect(() => {
    if (status !== "loading") {
      return;
    }
    let cancelled = false;
    loadGoogleMaps(BROWSER_KEY)
      .then((g) => {
        if (cancelled || !canvasRef.current) {
          return;
        }
        const init = initialRef.current;
        apiRef.current = g;
        mapRef.current = new g.Map(canvasRef.current, {
          center: init.center ?? US_CENTER,
          zoom: init.center ? 10 : 4,
          mapTypeId: "roadmap",
          // Controls as in marketing's example: Map | Satellite top-left; fullscreen, camera and
          // Street View at Google's default corners (top-right / bottom-right).
          mapTypeControl: true,
          mapTypeControlOptions: {
            style: g.MapTypeControlStyle.HORIZONTAL_BAR,
            position: g.ControlPosition.BLOCK_START_INLINE_START,
          },
          fullscreenControl: true,
          streetViewControl: true,
          cameraControl: true,
          clickableIcons: false,
          gestureHandling: "auto",
        });
        infoRef.current = new g.InfoWindow({
          pixelOffset: new g.Size(0, -PIN_SIZE / 2),
          maxWidth: 260,
        });
        setStatus(googleMapsAuthFailed() ? "unavailable" : "ready");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("unavailable");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  // Pins + fit to results whenever the result set changes.
  useEffect(() => {
    const g = apiRef.current;
    const map = mapRef.current;
    if (status !== "ready" || !g || !map) {
      return;
    }
    const Pin = pinClass(g);
    const overlays = overlaysRef.current;
    infoRef.current?.close();
    pins.forEach((p) => {
      const pos = { lat: p.displayLat, lng: p.displayLng };
      const pin = new Pin(pos, p.n, `${p.n}. ${p.name}`, () => {
        onSelectRef.current(p.n);
        const info = infoRef.current;
        if (info) {
          info.setContent(
            popupContent({
              n: p.n,
              name: p.name,
              approximate: p.approximate,
              line: addressLine(p),
            }),
          );
          info.setPosition(pos);
          info.open({ map });
        }
      });
      pin.setMap(map);
      overlays.set(p.n, pin);
    });

    let capZoom: google.maps.MapsEventListener | null = null;
    if (pins.length > 0) {
      const bounds = new g.LatLngBounds();
      pins.forEach((p) =>
        bounds.extend({ lat: p.displayLat, lng: p.displayLng }),
      );
      map.fitBounds(bounds, 48);
      capZoom = g.event.addListenerOnce(map, "idle", () => {
        const z = map.getZoom();
        if (z !== undefined && z > MAX_FIT_ZOOM) {
          map.setZoom(MAX_FIT_ZOOM);
        }
      });
    } else if (center) {
      map.setCenter(center);
      map.setZoom(10);
    }

    return () => {
      // an unfired one-shot cap would otherwise snap a later manual zoom back to 13
      capZoom?.remove();
      overlays.forEach((o) => o.setMap(null));
      overlays.clear();
    };
  }, [status, pins, center]);

  // Card <-> pin: highlight the active pin and bring it into view.
  useEffect(() => {
    if (status !== "ready") {
      return;
    }
    overlaysRef.current.forEach((o, n) => o.setActive(n === activeN));
    const active = pins.find((p) => p.n === activeN);
    if (active && mapRef.current) {
      mapRef.current.panTo({ lat: active.displayLat, lng: active.displayLng });
    }
  }, [status, activeN, pins]);

  if (status === "unavailable") {
    return <MapUnavailable />;
  }

  return (
    <div className="vl-map">
      <div
        ref={canvasRef}
        className="vl-map-canvas"
        data-testid="voucher-locator-map"
        data-map-status={status}
      />
    </div>
  );
};

const MapUnavailable: React.FC = () => (
  <div
    className="vl-map vl-map--unavailable"
    role="status"
    data-testid="voucher-locator-map-unavailable"
  >
    The map can&rsquo;t be shown right now. Every location is still listed with
    its address and phone number.
  </div>
);

/**
 * Map-local containment (external audit P1, be46519e): an unexpected exception from Google or from
 * our overlay code must cost the MAP only. Without this, React unwinds to the route error boundary
 * ("Unexpected Application Error!") and the results list disappears with it.
 */
class MapErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    console.error("[LocatorMap] map failed; showing the list only", error);
  }

  render(): React.ReactNode {
    return this.state.failed ? <MapUnavailable /> : this.props.children;
  }
}

const LocatorMap: React.FC<Props> = (props) => (
  <MapErrorBoundary>
    <LocatorMapInner {...props} />
  </MapErrorBoundary>
);

export default LocatorMap;
